import { apiRequest } from '@/lib/api';
import {
  putFileToPresignedUrl,
  validateNavImageFile,
} from "@/lib/api/navImageUpload";

// Types for Programs V2 API
export interface ProgramV2 {
  _id: string;
  programId?: string;
  title: string;
  subtitle?: string;
  description: string;
  videoUrl?: string;
  language?: string;
  status?: string;
  category?: {
    _id: string;
    title: string;
    description?: string;
    categoryId?: string;
    field?: string;
  };
  programType?: string;
  createdAt?: string;
  updatedAt?: string;
  keyFeatures?: Array<{
    id: number;
    name: string;
    icon: string;
    value: string;
    descriptionHeading?: string;
    description?: string;
  }>;
  payAsYouGo?: boolean;
  moduleConfiguration?: {
    payAsYouGo: boolean;
    modules: Array<{
      _id?: string;
      name: string;
      description?: string;
      programId?: string;
      price: number;
      isActive: boolean;
      order: number;
      metadata?: {
        duration?: number;
        difficulty?: 'BEGINNER' | 'INTERMEDIATE' | 'ADVANCED';
        tags?: string[];
        videoUrl?: string;
        resources?: Array<{
          type: 'PDF' | 'VIDEO' | 'AUDIO' | 'LINK';
          title: string;
          url: string;
        }>;
      };
      prerequisites?: string[];
      estimatedCompletionTime?: number;
      isExam?: boolean;
      examDetails?: {
        passingScore: number;
        timeLimit?: number;
        questions?: number;
      };
    }>;
    pricingStrategy?: 'INDIVIDUAL' | 'BUNDLE';
    bundleDiscount?: number;
    minimumModulesForBundle?: number;
  };
  modulePricingMetadata?: {
    totalModules: number;
    totalPrice: number;
    averageModulePrice: number;
    bundlePrice?: number;
    savingsWithBundle?: number;
  };
  // Additional fields for student frontend
  slug?: string;
  navThumbnailUrl?: string | null;
  thumbnail?: string;
  banner?: string;
  level?: string;
  instructor?: string;
  price?: number;
  features?: string[];
  learningOutcomes?: string[];
  prerequisites?: string[];
  faqs?: Array<{
    id: number;
    type: string;
    question: string;
    answer: string;
  }>;
  clinicalProcedures?: Array<{
    id: string;
    title: string;
  }>;
  nationalCertifications?: {
    isallIncluded: boolean;
    items: string[];
  };
  // Additional fields for transform function
  about?: string;
  categoryId?: {
    _id: string;
    title: string;
    description?: string;
    categoryId?: string;
    field?: string;
  } | string;
  duration?: {
    value: number;
    isInWeeks: boolean;
  };
  availableProgramTypes?: any[];
  defaultProgramType?: {
    type: string;
  };
  thisCourseIncludesData?: any[];
  whoisthisFor?: any[];
  wherecaniWork?: any[];
  clinicalProceduresGrouped?: any[];
  podcastData?: any[];
  programComparison?: any[];
  premiumDescription?: string;
  premiumTagline?: string;
  marketingTags?: string[];
  premiumRating?: number;
  premiumReviewCount?: number;
  premiumStudentCount?: number;
  premiumViewCount?: number;
  premiumLikeCount?: number;
  premiumShareCount?: number;
  flyerTheme?: 'black' | 'red' | 'blue' | 'green' | 'purple' | 'rose';
  socialMedia?: any;
  modules?: any[];
  platform?: any;
}

/** Backend may use either naming style in `data.pagination`. */
export interface ProgramsV2Pagination {
  currentPage?: number;
  page?: number;
  totalPages?: number;
  pages?: number;
  totalCount?: number;
  total?: number;
  limit?: number;
  hasNextPage?: boolean;
  hasPrevPage?: boolean;
}

export interface ProgramsV2Response {
  success: boolean;
  message: string;
  data: {
    programs: ProgramV2[];
    pagination?: ProgramsV2Pagination;
  };
}

export interface ProgramV2Response {
  success: boolean;
  message: string;
  data: {
    program: ProgramV2;
  };
}

export interface ProgramsV2Params {
  categoryId?: string;
  programType?: string;
  /** `all` = no status filter (active + inactive). Omit or empty defaults to active-only on the server. */
  status?: 'active' | 'inactive' | 'all' | string;
  language?: string;
  search?: string;
  page?: number;
  limit?: number;
  sortBy?: 'title' | 'createdAt' | 'updatedAt' | 'price';
  sortOrder?: 'asc' | 'desc';
}

// Public API functions for student frontend
export async function fetchAllProgramsV2(params: ProgramsV2Params = {}): Promise<ProgramsV2Response> {
  const queryParams = new URLSearchParams();
  
  if (params.categoryId) queryParams.append('categoryId', params.categoryId);
  if (params.programType) queryParams.append('programType', params.programType);
  if (params.status) queryParams.append('status', params.status);
  if (params.language) queryParams.append('language', params.language);
  if (params.search) queryParams.append('search', params.search);
  if (params.page) queryParams.append('page', params.page.toString());
  if (params.limit) queryParams.append('limit', params.limit.toString());
  if (params.sortBy) queryParams.append('sortBy', params.sortBy);
  if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
  
  const url = `/api/programs-v2${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  return apiRequest(url);
}

export async function fetchProgramV2ById(programId: string): Promise<ProgramV2Response> {
  return apiRequest(`/api/programs-v2/${programId}`);
}

export async function fetchProgramV2ByIdAdmin(programId: string): Promise<ProgramV2Response> {
  return apiRequest(`/api/programs-v2/admin/${programId}`);
}

export interface CloneProgramV2Response {
  success: boolean;
  message: string;
  data?: {
    _id: string;
    programId?: string;
    slug?: string;
    title?: string;
    status?: string;
    isPublished?: boolean;
    programTypes?: string[];
  };
  error?: string;
}

export async function cloneProgramV2(programId: string): Promise<CloneProgramV2Response> {
  return apiRequest(`/api/programs-v2/${programId}/clone`, {
    method: 'POST',
  });
}

export async function updateProgramV2(programId: string, data: {
  program?: Partial<ProgramV2>;
  programTypes?: any[];
}): Promise<ProgramV2Response> {
  return apiRequest(`/api/programs-v2/update/${programId}`, {
    method: 'PUT',
    body: data
  });
}

// Program type image upload (presigned URL flow)
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'] as const;
const MAX_IMAGE_SIZE_BYTES = 5 * 1024 * 1024; // 5 MB

export interface ProgramTypeImageUploadUrlRequest {
  fileName: string;
  fileType: string;
  fileSize?: number;
}

export interface ProgramTypeImageUploadUrlResponse {
  success: boolean;
  data?: {
    uploadUrl: string;
    imageUrl: string;
    expiresIn: number;
  };
  error?: string;
}

export async function getProgramTypeImageUploadUrl(
  params: ProgramTypeImageUploadUrlRequest
): Promise<ProgramTypeImageUploadUrlResponse> {
  const response = await apiRequest<ProgramTypeImageUploadUrlResponse>(
    '/api/programs-v2/program-type-image/upload-url',
    {
      method: 'POST',
      body: params,
    }
  );
  return response;
}

/**
 * Upload a file using the presigned URL flow. Returns the permanent imageUrl on success.
 * Validates file type and size before calling the API.
 */
export async function uploadProgramTypeImage(file: File): Promise<string> {
  const fileType = file.type?.toLowerCase();
  if (!fileType || !ALLOWED_IMAGE_TYPES.includes(fileType as any)) {
    throw new Error(
      'Invalid file type. Allowed: JPEG, JPG, PNG, WebP.'
    );
  }
  if (file.size > MAX_IMAGE_SIZE_BYTES) {
    throw new Error('File is too large. Max size is 5 MB.');
  }

  const { data, success, error } = await getProgramTypeImageUploadUrl({
    fileName: file.name,
    fileType: file.type,
    fileSize: file.size,
  });

  if (!success || !data?.uploadUrl || !data?.imageUrl) {
    throw new Error(error || 'Failed to get upload URL');
  }

  await putFileToPresignedUrl(data.uploadUrl, file);

  return data.imageUrl;
}

export async function getProgramNavThumbnailUploadUrl(
  params: ProgramTypeImageUploadUrlRequest,
): Promise<ProgramTypeImageUploadUrlResponse> {
  return apiRequest<ProgramTypeImageUploadUrlResponse>(
    "/api/programs-v2/program-type-nav-thumbnail/upload-url",
    {
      method: "POST",
      body: params,
    },
  );
}

/** Upload a 1:1 mega-menu thumbnail; persist URL on program root as `navThumbnailUrl`. */
export async function uploadProgramNavThumbnail(file: File): Promise<string> {
  validateNavImageFile(file);

  const { data, success, error } = await getProgramNavThumbnailUploadUrl({
    fileName: file.name,
    fileType: file.type,
    fileSize: file.size,
  });

  if (!success || !data?.uploadUrl || !data?.imageUrl) {
    throw new Error(error || "Failed to get upload URL");
  }

  await putFileToPresignedUrl(data.uploadUrl, file);
  return data.imageUrl;
}

// Export all V2 program API functions as a single object
export const programsV2Api = {
  fetchAllPrograms: fetchAllProgramsV2,
  fetchProgramById: fetchProgramV2ById,
  fetchProgramByIdAdmin: fetchProgramV2ByIdAdmin,
  cloneProgram: cloneProgramV2,
  updateProgram: updateProgramV2,
  getProgramTypeImageUploadUrl,
  uploadProgramTypeImage,
  getProgramNavThumbnailUploadUrl,
  uploadProgramNavThumbnail,
};
